<template>
  <div class="modal-card editor-certificado">
    <header class="modal-card-head">
      <div class="modal-card-title">
        <p class="is-size-7 has-text-grey mb-0">{{ t("Plantilla de regalo", "Gift template") }}</p>
        <p class="is-size-5 has-text-weight-bold">{{ cupon ? cupon.Nombre : "" }}</p>
        <div class="tags mt-1 mb-0" v-if="cupon">
          <b-tag type="is-light">{{ cupon.TipoCupon || t("sin tipo", "no type") }}</b-tag>
          <b-tag type="is-light">{{ t("Versión", "Version") }} {{ guardado.Version || 0 }}</b-tag>
          <b-tag :type="config.Activo ? 'is-success' : 'is-warning is-light'">
            {{ config.Activo ? t("Envío con esta configuración", "Sending with this setup") : t("Envío anterior (sin cambios)", "Previous sending (unchanged)") }}
          </b-tag>
        </div>
      </div>
      <button class="delete" aria-label="cerrar" @click="cerrar"></button>
    </header>

    <section class="modal-card-body">
      <div v-if="cargando" class="has-text-centered py-6">
        <b-icon icon="spinner" pack="fas" custom-class="fa-spin"></b-icon>
      </div>
      <template v-else-if="cupon">
        <b-message v-if="errores.length" type="is-danger" :closable="false" class="mb-3">
          <p v-for="(e, i) in errores" :key="i">{{ e }}</p>
        </b-message>

        <b-tabs v-model="pestana" :animated="false" class="mb-0">
          <!-- ------------------------------------------------ General -->
          <b-tab-item :label="t('General', 'General')" icon="sliders-h" icon-pack="fas">
            <div class="columns is-multiline">
              <div class="column is-12">
                <div class="box interruptor" :class="{ encendido: config.Activo }">
                  <b-switch v-model="config.Activo" type="is-success" @input="confirmarActivo">
                    <b>{{ t("Usar esta configuración para enviar", "Use this setup when sending") }}</b>
                  </b-switch>
                  <p class="is-size-7 mt-2">
                    {{
                      config.Activo
                        ? t(
                            "Los envíos de HubSpot y del capturador con este tipo salen con este PDF, este correo y este remitente. Si algo falla al armarlo, sale como antes.",
                            "HubSpot and capturer sends of this type go out with this PDF, email and sender. If anything fails, they go out as before."
                          )
                        : t(
                            "Apagado: HubSpot y el capturador siguen enviando como hasta hoy. Prepara y prueba todo con «Enviarme una prueba» antes de encenderlo.",
                            "Off: HubSpot and the capturer keep sending as they do today. Prepare and test with «Send me a test» before turning it on."
                          )
                    }}
                  </p>
                </div>
              </div>

              <div class="column is-12">
                <p class="subtitle is-6 mb-2"><b>{{ t("Disponible para", "Available for") }}</b></p>
                <p class="is-size-7 has-text-grey mb-3">
                  {{ t("Define quién puede ver y usar esta plantilla.", "Defines who can see and use this template.") }}
                </p>
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <b-switch v-model="config.DisponiblePara.CampanasQr">{{ t("Campañas QR", "QR campaigns") }}</b-switch>
                    <p class="is-size-7 has-text-grey">{{ t("Aparece al elegir el regalo de una campaña QR (las campañas siempre envían con esta configuración).", "Shows up when choosing a QR campaign's gift (campaigns always send with this setup).") }}</p>
                  </div>
                  <div class="column is-6">
                    <p class="label is-small mb-1">{{ t("HubSpot (tipo de regalo)", "HubSpot (gift type)") }}</p>
                    <b-field>
                      <b-checkbox-button v-for="g in catalogo.giftTypes" :key="g" v-model="config.DisponiblePara.HubSpot" :native-value="g" size="is-small" type="is-info">
                        {{ etiquetaGift(g) }}
                      </b-checkbox-button>
                    </b-field>
                  </div>
                  <div class="column is-6">
                    <p class="label is-small mb-1">{{ t("Clubes que lo ven", "Clubs that see it") }}</p>
                    <multiselect
                      v-model="clubesElegidos"
                      :options="catalogo.clubes"
                      :multiple="true"
                      track-by="Id"
                      label="Nombre"
                      :placeholder="t('Todos (ninguno elegido)', 'All (none chosen)')"
                      :close-on-select="false"
                      select-label=""
                      deselect-label=""
                    />
                  </div>
                  <div class="column is-6">
                    <p class="label is-small mb-1">{{ t("Distribuidores (programas)", "Distributors (programs)") }}</p>
                    <multiselect
                      v-model="distribuidoresElegidos"
                      :options="distribuidoresOpciones"
                      :multiple="true"
                      track-by="Id"
                      label="Nombre"
                      :placeholder="t('Ninguno', 'None')"
                      :close-on-select="false"
                      select-label=""
                      deselect-label=""
                    />
                  </div>
                </div>
              </div>

              <div class="column is-12"><hr class="my-1" /></div>
              <div class="column is-4">
                <b-field :label="t('Formato de fecha', 'Date format')" label-position="on-border">
                  <b-select v-model="config.FormatoFecha" expanded>
                    <option value="MM/DD/YYYY">{{ t("Mes/día/año (12/31/2026)", "Month/day/year (12/31/2026)") }}</option>
                    <option value="DD/MM/YYYY">{{ t("Día/mes/año (31/12/2026)", "Day/month/year (31/12/2026)") }}</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-8">
                <p class="label is-small mb-1">{{ t("Plazos (desde la emisión)", "Deadlines (from issue date)") }}</p>
                <div class="columns is-mobile is-multiline is-variable is-1">
                  <div class="column is-6-mobile is-3" v-for="p in plazos" :key="p.clave">
                    <b-field :label="p.etiqueta" label-position="on-border">
                      <b-input v-model.number="config.Plazos[p.clave]" type="number" min="0" :placeholder="String(catalogo.plazos[p.clave])" size="is-small" />
                    </b-field>
                  </div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- ------------------------------------------------ PDF -->
          <b-tab-item :label="t('PDF y posiciones', 'PDF & positions')" icon="file-pdf" icon-pack="fas">
            <div class="is-flex is-flex-wrap-wrap is-align-items-center mb-3 barra">
              <b-field class="mb-0 mr-3">
                <b-radio-button v-model="idiomaPdf" native-value="es" size="is-small">Español</b-radio-button>
                <b-radio-button v-model="idiomaPdf" native-value="en" size="is-small">English</b-radio-button>
              </b-field>
              <span class="is-size-7 has-text-grey mr-3" v-if="guardado.Pdf && guardado.Pdf[idiomaPdf]">
                {{ guardado.Pdf[idiomaPdf].NombreOriginal || "PDF" }} · {{ guardado.Pdf[idiomaPdf].Paginas.length }} {{ t("pág.", "pg.") }}
              </span>
              <b-upload v-model="archivoPdf" accept="application/pdf" class="mr-2" @input="subirPdf">
                <a class="button is-small is-info is-light" :class="{ 'is-loading': subiendoPdf }">
                  <b-icon icon="upload" pack="fas" size="is-small"></b-icon>
                  <span>{{ guardado.Pdf && guardado.Pdf[idiomaPdf] ? t("Reemplazar PDF", "Replace PDF") : t("Subir PDF", "Upload PDF") }}</span>
                </a>
              </b-upload>
              <b-button size="is-small" icon-left="copy" icon-pack="fas" class="mr-2" :disabled="!camposDe(otroIdioma).length || !tienePdf(idiomaPdf)" @click="copiarPosiciones">
                {{ t("Copiar posiciones del otro idioma", "Copy positions from other language") }}
              </b-button>
              <b-button size="is-small" type="is-primary is-light" icon-left="eye" icon-pack="fas" :loading="generandoVista" :disabled="!tienePdf(idiomaPdf)" @click="verPdf">
                {{ t("Vista previa con datos de ejemplo", "Preview with sample data") }}
              </b-button>
            </div>
            <div v-if="!tienePdf(idiomaPdf)" class="notification is-light has-text-centered">
              {{ t("Sube el PDF de este idioma para acomodar los datos.", "Upload this language's PDF to place the fields.") }}
            </div>
            <acomodador-pdf
              v-else
              :key="`${idiomaPdf}-${(guardado.Pdf[idiomaPdf] || {}).Archivo}`"
              v-model="camposIdioma"
              :pdf="pdfDatos[idiomaPdf]"
              :tamanos="guardado.Pdf[idiomaPdf].Paginas"
              :idioma="idiomaPdf"
              :datos="catalogo.datos"
              :ejemplo="catalogo.ejemplo"
              :iso="iso"
            />
          </b-tab-item>

          <!-- ------------------------------------------------ Correo -->
          <b-tab-item :label="t('Correo', 'Email')" icon="envelope" icon-pack="fas">
            <div class="columns is-multiline">
              <div class="column is-4">
                <b-field :label="t('Nombre del remitente', 'Sender name')" label-position="on-border">
                  <b-input v-model="config.Correo.RemitenteNombre" placeholder="Vacancy Rewards" maxlength="80" :has-counter="false" />
                </b-field>
              </div>
              <div class="column is-4">
                <b-field :label="t('Correo del remitente', 'Sender email')" label-position="on-border">
                  <b-select v-model="config.Correo.RemitenteCorreo" expanded>
                    <option value="">{{ catalogo.remitentes[0] }} ({{ t("el de siempre", "default") }})</option>
                    <option v-for="r in catalogo.remitentes.slice(1)" :key="r" :value="r">{{ r }}</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-4">
                <b-field :label="t('Responder a (opcional)', 'Reply to (optional)')" label-position="on-border">
                  <b-input v-model="config.Correo.ResponderA" type="email" placeholder="soporte@vacancyrewards.com" />
                </b-field>
              </div>
              <div class="column is-12 py-0">
                <b-switch v-model="config.Correo.AdjuntarPdf" size="is-small">{{ t("Adjuntar el PDF del certificado", "Attach the certificate PDF") }}</b-switch>
                <span class="is-size-7 has-text-grey ml-2">{{ t("Solo se puede enviar desde correos verificados en Mailtrap.", "Only Mailtrap-verified senders can be used.") }}</span>
              </div>

              <div class="column is-12">
                <b-field class="mb-2">
                  <b-radio-button v-model="idiomaCorreo" native-value="es" size="is-small">Español</b-radio-button>
                  <b-radio-button v-model="idiomaCorreo" native-value="en" size="is-small">English</b-radio-button>
                </b-field>
                <div class="columns">
                  <div class="column is-6">
                    <b-field :label="t('Asunto', 'Subject')" label-position="on-border" :message="t('Vacío: se usa el asunto que manda cada proceso (HubSpot, capturador…).', 'Empty: each process keeps its own subject.')">
                      <b-input v-model="config.Correo.Asunto[idiomaCorreo]" />
                    </b-field>
                    <div class="variables mb-2">
                      <span class="is-size-7 has-text-grey mr-1">{{ t("Insertar:", "Insert:") }}</span>
                      <b-tag v-for="v in catalogo.variables" :key="v" class="variable" type="is-info is-light" @click.native="insertarVariable(v)">{{ llaves(v) }}</b-tag>
                    </div>
                    <textarea
                      ref="html"
                      v-model="config.Correo.Html[idiomaCorreo]"
                      class="textarea codigo-html"
                      rows="18"
                      spellcheck="false"
                      :placeholder="t('Pega aquí el HTML del correo…', 'Paste the email HTML here…')"
                    ></textarea>
                    <div class="buttons mt-2">
                      <b-upload accept=".html,.htm,text/html" @input="cargarHtml">
                        <a class="button is-small"><b-icon icon="file-code" pack="fas" size="is-small"></b-icon><span>{{ t("Cargar archivo .html", "Load .html file") }}</span></a>
                      </b-upload>
                      <b-upload accept="image/png,image/jpeg,image/webp,image/gif" @input="subirImagen">
                        <a class="button is-small" :class="{ 'is-loading': subiendoImagen }"><b-icon icon="image" pack="fas" size="is-small"></b-icon><span>{{ t("Agregar imagen", "Add image") }}</span></a>
                      </b-upload>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="is-flex is-justify-content-space-between is-align-items-center mb-1">
                      <span class="is-size-7 has-text-grey">{{ t("Vista previa con datos de ejemplo", "Preview with sample data") }}</span>
                      <b-button size="is-small" type="is-success is-light" icon-left="paper-plane" icon-pack="fas" :loading="enviandoPrueba" @click="enviarPrueba">
                        {{ t("Enviarme una prueba", "Send me a test") }}
                      </b-button>
                    </div>
                    <p class="asunto-previo"><b>{{ t("Asunto:", "Subject:") }}</b> {{ previa.asunto || t("(el de cada proceso)", "(each process' own)") }}</p>
                    <iframe class="previa-correo" sandbox="" :srcdoc="previa.html || vacio" title="Vista previa del correo"></iframe>
                  </div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- ------------------------------------------------ Historial -->
          <b-tab-item :label="t('Historial y envíos', 'History & sends')" icon="history" icon-pack="fas">
            <div class="columns">
              <div class="column is-5">
                <p class="subtitle is-6"><b>{{ t("Versiones", "Versions") }}</b></p>
                <div class="lista-versiones">
                  <div v-for="v in versiones" :key="v.Version" class="version">
                    <div>
                      <b>v{{ v.Version }}</b> · {{ fecha(v.Fecha) }} · {{ v.Usuario }}
                      <p class="is-size-7 has-text-grey">{{ v.Resumen }}</p>
                    </div>
                    <b-button v-if="v.Version !== guardado.Version" size="is-small" @click="restaurar(v.Version)">{{ t("Restaurar", "Restore") }}</b-button>
                  </div>
                  <p v-if="!versiones.length" class="has-text-grey is-size-7">{{ t("Sin cambios guardados todavía.", "No saved changes yet.") }}</p>
                </div>
              </div>
              <div class="column is-7">
                <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                  <p class="subtitle is-6 mb-0"><b>{{ t("A quién se envió", "Who received it") }}</b> ({{ emisiones.total }})</p>
                  <b-input v-model="emisiones.busqueda" size="is-small" icon="search" icon-pack="fas" :placeholder="t('Correo, código o nombre', 'Email, code or name')" @input="buscarEmisiones" />
                </div>
                <b-table
                  :data="emisiones.items"
                  :loading="emisiones.cargando"
                  paginated
                  backend-pagination
                  :total="emisiones.total"
                  :per-page="15"
                  :current-page.sync="emisiones.pagina"
                  pagination-simple
                  narrowed
                  striped
                  @page-change="listarEmisiones"
                >
                  <b-table-column :label="t('Fecha', 'Date')" v-slot="p">{{ fecha(p.row.Fecha) }}</b-table-column>
                  <b-table-column :label="t('Código', 'Code')" v-slot="p"><code>{{ p.row.Codigo }}</code></b-table-column>
                  <b-table-column :label="t('Para', 'To')" v-slot="p">
                    {{ p.row.Nombre }}<br /><span class="is-size-7 has-text-grey">{{ p.row.Email }}</span>
                  </b-table-column>
                  <b-table-column :label="t('Origen', 'Source')" v-slot="p">{{ etiquetaOrigen(p.row.Origen) }}</b-table-column>
                  <b-table-column :label="t('Envío', 'Sent')" v-slot="p">
                    <b-tooltip :label="p.row.Error || (p.row.Via === 'configurado' ? `v${p.row.Version}` : t('envío anterior', 'previous'))" type="is-dark">
                      <b-tag :type="p.row.CorreoEnviado ? 'is-success is-light' : 'is-danger is-light'">
                        {{ p.row.CorreoEnviado ? "✓" : "✗" }} {{ p.row.PdfAdjunto ? "PDF" : t("sin PDF", "no PDF") }}
                      </b-tag>
                    </b-tooltip>
                  </b-table-column>
                  <template #empty>
                    <p class="has-text-centered has-text-grey is-size-7 py-4">{{ t("Aún no hay envíos registrados.", "No sends recorded yet.") }}</p>
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab-item>
        </b-tabs>
      </template>
    </section>

    <footer class="modal-card-foot is-justify-content-space-between">
      <span class="is-size-7" :class="sucio ? 'has-text-warning-dark' : 'has-text-grey'">
        {{ sucio ? t("Hay cambios sin guardar", "Unsaved changes") : t("Todo guardado", "All saved") }}
      </span>
      <div class="buttons">
        <b-button @click="cerrar">{{ t("Cerrar", "Close") }}</b-button>
        <b-button type="is-success" icon-left="save" icon-pack="fas" :loading="guardando" :disabled="!sucio" @click="guardar">{{ t("Guardar", "Save") }}</b-button>
      </div>
    </footer>

    <b-modal v-model="vistaPdf.abierta" has-modal-card :can-cancel="['escape', 'x', 'outside']" @close="cerrarVistaPdf">
      <div class="modal-card" style="width: min(1100px, 96vw); height: 90vh">
        <header class="modal-card-head py-2">
          <p class="modal-card-title is-size-6">{{ t("Vista previa con datos de ejemplo", "Preview with sample data") }}</p>
          <button class="delete" @click="cerrarVistaPdf"></button>
        </header>
        <section class="modal-card-body p-0">
          <iframe v-if="vistaPdf.url" :src="vistaPdf.url" title="PDF" style="width: 100%; height: 100%; border: 0"></iframe>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import AcomodadorPdf from "./AcomodadorPdf.vue";

const API_URL = process.env.VUE_APP_API_URL;
const URL_CUPONES = `${API_URL}/cupones`;

const configVacia = () => ({
  Campos: [],
  FormatoFecha: "MM/DD/YYYY",
  Plazos: {},
  Correo: { Asunto: { es: "", en: "" }, Html: { es: "", en: "" }, AdjuntarPdf: true, RemitenteNombre: "", RemitenteCorreo: "", ResponderA: "" },
  DisponiblePara: { CampanasQr: false, Clubes: [], Distribuidores: [], HubSpot: [] },
  Activo: false,
});

// Solo lo que se guarda con "Guardar" (el PDF se sube aparte)
const editable = (c = {}) => {
  const base = configVacia();
  return {
    Campos: (c.Campos || []).map((x) => ({ ...x })),
    FormatoFecha: c.FormatoFecha || base.FormatoFecha,
    Plazos: { ...(c.Plazos || {}) },
    Correo: {
      ...base.Correo,
      ...(c.Correo || {}),
      Asunto: { ...base.Correo.Asunto, ...(c.Correo?.Asunto || {}) },
      Html: { ...base.Correo.Html, ...(c.Correo?.Html || {}) },
    },
    DisponiblePara: { ...base.DisponiblePara, ...(c.DisponiblePara || {}) },
    Activo: Boolean(c.Activo),
  };
};

const limpioParaComparar = (c) => {
  const plazos = Object.fromEntries(Object.entries(c.Plazos || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));
  return JSON.stringify({ ...c, Plazos: plazos });
};

export default {
  name: "CertificadoEditor",
  components: { AcomodadorPdf },
  props: { cuponId: { type: String, required: true } },
  data() {
    return {
      iso: localStorage.getItem("iso") || "es",
      cargando: true,
      guardando: false,
      errores: [],
      pestana: 0,
      cupon: null,
      guardado: {},
      config: configVacia(),
      original: "",
      versiones: [],
      catalogo: { datos: [], variables: [], giftTypes: [], plazos: {}, remitentes: [], ejemplo: {}, clubes: [], distribuidores: [] },
      idiomaPdf: "es",
      idiomaCorreo: "es",
      pdfDatos: { es: null, en: null },
      archivoPdf: null,
      subiendoPdf: false,
      subiendoImagen: false,
      enviandoPrueba: false,
      generandoVista: false,
      vistaPdf: { abierta: false, url: null },
      previa: { asunto: "", html: "" },
      pausaPrevia: null,
      emisiones: { items: [], total: 0, pagina: 1, busqueda: "", cargando: false, pausa: null },
      vacio: "<p style='font-family:sans-serif;color:#999;padding:16px'>Sin HTML todavía</p>",
    };
  },
  computed: {
    sucio() {
      return Boolean(this.cupon) && limpioParaComparar(this.config) !== this.original;
    },
    otroIdioma() {
      return this.idiomaPdf === "es" ? "en" : "es";
    },
    camposIdioma: {
      get() {
        return this.config.Campos.filter((c) => c.Idioma === this.idiomaPdf);
      },
      set(campos) {
        this.config.Campos = [...this.config.Campos.filter((c) => c.Idioma !== this.idiomaPdf), ...campos];
      },
    },
    clubesElegidos: {
      get() {
        return this.catalogo.clubes.filter((c) => this.config.DisponiblePara.Clubes.includes(c.Id));
      },
      set(lista) {
        this.config.DisponiblePara.Clubes = lista.map((c) => c.Id);
      },
    },
    distribuidoresOpciones() {
      const clubes = this.config.DisponiblePara.Clubes;
      const nombreClub = (id) => this.catalogo.clubes.find((c) => c.Id === id)?.Nombre;
      return this.catalogo.distribuidores
        .filter((d) => !clubes.length || clubes.includes(d.ClubId) || this.config.DisponiblePara.Distribuidores.includes(d.Id))
        .map((d) => ({ ...d, Nombre: nombreClub(d.ClubId) ? `${d.Nombre} · ${nombreClub(d.ClubId)}` : d.Nombre }));
    },
    distribuidoresElegidos: {
      get() {
        return this.distribuidoresOpciones.filter((d) => this.config.DisponiblePara.Distribuidores.includes(d.Id));
      },
      set(lista) {
        this.config.DisponiblePara.Distribuidores = lista.map((d) => d.Id);
      },
    },
    plazos() {
      return [
        { clave: "ActivarDias", etiqueta: this.t("Activar (días)", "Activate (days)") },
        { clave: "CancelarDias", etiqueta: this.t("Cancelar (días)", "Cancel (days)") },
        { clave: "UltimaReservaMeses", etiqueta: this.t("Reservar (meses)", "Book by (months)") },
        { clave: "VenceMeses", etiqueta: this.t("Vence (meses)", "Expires (months)") },
      ];
    },
  },
  watch: {
    "config.Correo": { deep: true, handler: "previaConPausa" },
    idiomaCorreo: "previaConPausa",
    pestana(p) {
      if (p === 2) this.actualizarPrevia();
      if (p === 3 && !this.emisiones.items.length) this.listarEmisiones(1);
    },
  },
  async created() {
    await this.cargar();
  },
  beforeDestroy() {
    clearTimeout(this.pausaPrevia);
    if (this.vistaPdf.url) URL.revokeObjectURL(this.vistaPdf.url);
  },
  methods: {
    t(es, en) {
      return this.iso === "en" ? en : es;
    },
    llaves(v) {
      return `{{${v}}}`;
    },
    fecha(ms) {
      return ms ? moment(ms).locale(this.iso).format("lll") : "—";
    },
    etiquetaGift(g) {
      return { completada: this.t("Completada", "Completed"), nocompletada: this.t("No completada", "Not completed"), sinregalo: this.t("Sin regalo", "No gift") }[g] || g;
    },
    etiquetaOrigen(o) {
      return { capturador: this.t("Capturador", "Capturer"), "campana-qr": this.t("Campaña QR", "QR campaign"), hubspot: "HubSpot", cupones: this.t("Cupones", "Coupons"), folio: "Folio" }[o] || this.t("Otro", "Other");
    },
    mensaje(err, porDefecto) {
      const d = err?.response?.data;
      if (d?.errores?.length) this.errores = d.errores;
      return d?.error || d?.message || porDefecto;
    },
    tienePdf(idioma) {
      return Boolean(this.guardado.Pdf?.[idioma]?.Archivo);
    },
    camposDe(idioma) {
      return this.config.Campos.filter((c) => c.Idioma === idioma);
    },
    async cargar() {
      this.cargando = true;
      try {
        const [catalogo, certificado] = await Promise.all([
          this.catalogo.datos.length ? Promise.resolve({ data: this.catalogo }) : axios.get(`${URL_CUPONES}/certificados/catalogo`),
          axios.get(`${URL_CUPONES}/${this.cuponId}/certificado`),
        ]);
        this.catalogo = catalogo.data;
        this.aplicarGuardado(certificado.data);
        await Promise.all(["es", "en"].map((i) => this.bajarPdf(i)));
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo abrir la plantilla", "Couldn't open template")), type: "is-danger" });
        this.$emit("close");
      } finally {
        this.cargando = false;
      }
    },
    aplicarGuardado(datos) {
      this.cupon = { _id: datos._id, Nombre: datos.Nombre, TipoCupon: datos.TipoCupon, Estado: datos.Estado };
      this.guardado = datos.Certificado || {};
      this.versiones = datos.Versiones || [];
      this.config = editable(this.guardado);
      this.original = limpioParaComparar(this.config);
    },
    async bajarPdf(idioma) {
      if (!this.tienePdf(idioma)) {
        this.pdfDatos[idioma] = null;
        return;
      }
      const { data } = await axios.get(`${URL_CUPONES}/${this.cuponId}/certificado/pdf/${idioma}`, { responseType: "arraybuffer" });
      this.pdfDatos[idioma] = data;
    },
    async subirPdf(archivo) {
      if (!archivo) return;
      this.subiendoPdf = true;
      try {
        const forma = new FormData();
        forma.append("archivo", archivo);
        const { data } = await axios.post(`${URL_CUPONES}/${this.cuponId}/certificado/pdf/${this.idiomaPdf}`, forma);
        // Se conserva lo que se estaba editando; los datos de páginas que ya no existen se quitan
        const sinGuardar = this.config;
        const paginas = data.Pdf.Paginas.length;
        this.guardado = { ...this.guardado, Pdf: { ...(this.guardado.Pdf || {}), [this.idiomaPdf]: data.Pdf }, Version: data.Version };
        sinGuardar.Campos = sinGuardar.Campos.filter((c) => c.Idioma !== this.idiomaPdf || c.Pagina < paginas);
        await this.bajarPdf(this.idiomaPdf);
        const { data: fresco } = await axios.get(`${URL_CUPONES}/${this.cuponId}/certificado`);
        this.versiones = fresco.Versiones || [];
        this.original = limpioParaComparar(editable(fresco.Certificado));
        this.config = sinGuardar;
        this.$buefy.toast.open({ message: this.t("PDF guardado", "PDF saved"), type: "is-success" });
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo subir el PDF", "Upload failed")), type: "is-danger", duration: 5000 });
      } finally {
        this.subiendoPdf = false;
        this.archivoPdf = null;
      }
    },
    copiarPosiciones() {
      const copia = this.camposDe(this.otroIdioma).map((c) => ({ ...c, Idioma: this.idiomaPdf }));
      const paginas = this.guardado.Pdf[this.idiomaPdf].Paginas.length;
      this.camposIdioma = copia.filter((c) => c.Pagina < paginas);
    },
    datosParaEnviar() {
      const plazos = Object.fromEntries(Object.entries(this.config.Plazos).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      return { ...this.config, Plazos: plazos };
    },
    async verPdf() {
      this.generandoVista = true;
      try {
        const { data } = await axios.post(`${URL_CUPONES}/${this.cuponId}/certificado/vista-previa/${this.idiomaPdf}`, this.datosParaEnviar(), { responseType: "blob" });
        if (this.vistaPdf.url) URL.revokeObjectURL(this.vistaPdf.url);
        this.vistaPdf = { abierta: true, url: URL.createObjectURL(data) };
      } catch (err) {
        let texto = this.t("No se pudo generar la vista previa", "Preview failed");
        try {
          texto = JSON.parse(await err.response.data.text()).error || texto;
        } catch (e) {
          // respuesta sin JSON
        }
        this.$buefy.toast.open({ message: texto, type: "is-danger", duration: 5000 });
      } finally {
        this.generandoVista = false;
      }
    },
    cerrarVistaPdf() {
      this.vistaPdf.abierta = false;
    },
    previaConPausa() {
      if (this.pestana !== 2) return;
      clearTimeout(this.pausaPrevia);
      this.pausaPrevia = setTimeout(this.actualizarPrevia, 600);
    },
    async actualizarPrevia() {
      try {
        const { data } = await axios.post(`${URL_CUPONES}/${this.cuponId}/certificado/correo/${this.idiomaCorreo}`, this.datosParaEnviar());
        this.previa = data;
        this.errores = [];
      } catch (err) {
        this.mensaje(err, "");
      }
    },
    insertarVariable(v) {
      const area = this.$refs.html;
      const texto = this.config.Correo.Html[this.idiomaCorreo] || "";
      const inicio = area ? area.selectionStart : texto.length;
      const fin = area ? area.selectionEnd : texto.length;
      this.config.Correo.Html[this.idiomaCorreo] = `${texto.slice(0, inicio)}{{${v}}}${texto.slice(fin)}`;
      this.$nextTick(() => {
        area?.focus();
        area?.setSelectionRange(inicio + v.length + 4, inicio + v.length + 4);
      });
    },
    cargarHtml(archivo) {
      if (!archivo) return;
      const lector = new FileReader();
      lector.onload = () => {
        this.config.Correo.Html[this.idiomaCorreo] = String(lector.result || "");
      };
      lector.readAsText(archivo);
    },
    async subirImagen(archivo) {
      if (!archivo) return;
      this.subiendoImagen = true;
      try {
        const forma = new FormData();
        forma.append("archivo", archivo);
        const { data } = await axios.post(`${URL_CUPONES}/certificados/imagen`, forma);
        const area = this.$refs.html;
        const texto = this.config.Correo.Html[this.idiomaCorreo] || "";
        const pos = area ? area.selectionStart : texto.length;
        const etiqueta = `<img src="${data.url}" alt="" style="max-width:100%;height:auto;" />`;
        this.config.Correo.Html[this.idiomaCorreo] = `${texto.slice(0, pos)}${etiqueta}${texto.slice(pos)}`;
        this.$buefy.toast.open({ message: this.t("Imagen agregada al HTML", "Image added to the HTML"), type: "is-success" });
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo subir la imagen", "Image upload failed")), type: "is-danger" });
      } finally {
        this.subiendoImagen = false;
      }
    },
    async enviarPrueba() {
      this.enviandoPrueba = true;
      try {
        const { data } = await axios.post(`${URL_CUPONES}/${this.cuponId}/certificado/prueba/${this.idiomaCorreo}`, this.datosParaEnviar());
        this.$buefy.toast.open({
          message: `${this.t("Prueba enviada a", "Test sent to")} ${data.enviadoA}${data.pdfAdjunto ? this.t(" (con PDF)", " (with PDF)") : ""}`,
          type: "is-success",
          duration: 5000,
        });
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo enviar la prueba", "Test failed")), type: "is-danger", duration: 5000 });
      } finally {
        this.enviandoPrueba = false;
      }
    },
    confirmarActivo(activo) {
      if (!activo) return;
      this.$buefy.dialog.confirm({
        title: this.t("¿Usar esta configuración?", "Use this setup?"),
        message: this.t(
          "Al guardar, los certificados de este tipo saldrán con este PDF y este correo (HubSpot, capturador, campañas). ¿Ya enviaste una prueba y la revisaste?",
          "When saved, certificates of this type will go out with this PDF and email. Did you send and check a test?"
        ),
        confirmText: this.t("Sí, usarla", "Yes, use it"),
        cancelText: this.t("Todavía no", "Not yet"),
        type: "is-warning",
        onCancel: () => {
          this.config.Activo = false;
        },
      });
    },
    async guardar() {
      this.guardando = true;
      this.errores = [];
      try {
        await axios.put(`${URL_CUPONES}/${this.cuponId}/certificado`, this.datosParaEnviar());
        const { data } = await axios.get(`${URL_CUPONES}/${this.cuponId}/certificado`);
        this.aplicarGuardado(data);
        this.$buefy.toast.open({ message: this.t("Plantilla guardada", "Template saved"), type: "is-success" });
        this.$emit("guardado");
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo guardar", "Save failed")), type: "is-danger", duration: 5000 });
      } finally {
        this.guardando = false;
      }
    },
    async restaurar(version) {
      try {
        const { data } = await axios.get(`${URL_CUPONES}/${this.cuponId}/certificado/versiones/${version}`);
        const copia = editable(data);
        // El PDF no se restaura (sigue el actual): solo posiciones, correo, usos y plazos
        const paginas = (i) => this.guardado.Pdf?.[i]?.Paginas?.length || 0;
        copia.Campos = copia.Campos.filter((c) => c.Pagina < paginas(c.Idioma));
        copia.Activo = this.config.Activo;
        this.config = copia;
        this.$buefy.toast.open({ message: this.t(`Versión ${version} cargada: revisa y guarda`, `Version ${version} loaded: review and save`), type: "is-info" });
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudo cargar la versión", "Couldn't load version")), type: "is-danger" });
      }
    },
    buscarEmisiones() {
      clearTimeout(this.emisiones.pausa);
      this.emisiones.pausa = setTimeout(() => this.listarEmisiones(1), 400);
    },
    async listarEmisiones(pagina) {
      this.emisiones.pagina = pagina || this.emisiones.pagina;
      this.emisiones.cargando = true;
      try {
        const { data } = await axios.get(`${URL_CUPONES}/${this.cuponId}/certificado/emisiones`, {
          params: { page: this.emisiones.pagina, limit: 15, busqueda: this.emisiones.busqueda || undefined },
        });
        this.emisiones.items = data.items;
        this.emisiones.total = data.total;
      } catch (err) {
        this.$buefy.toast.open({ message: this.mensaje(err, this.t("No se pudieron cargar los envíos", "Couldn't load sends")), type: "is-danger" });
      } finally {
        this.emisiones.cargando = false;
      }
    },
    cerrar() {
      if (!this.sucio) return this.$emit("close");
      this.$buefy.dialog.confirm({
        message: this.t("Hay cambios sin guardar. ¿Cerrar de todos modos?", "There are unsaved changes. Close anyway?"),
        confirmText: this.t("Cerrar sin guardar", "Close without saving"),
        cancelText: this.t("Seguir editando", "Keep editing"),
        type: "is-warning",
        onConfirm: () => this.$emit("close"),
      });
    },
  },
};
</script>

<style scoped>
.editor-certificado {
  width: min(1320px, 97vw);
  max-height: 96vh;
}
.modal-card-title {
  flex: 1;
  min-width: 0;
}
.interruptor {
  border-left: 4px solid #ffe08a;
}
.interruptor.encendido {
  border-left-color: #48c78e;
}
.codigo-html {
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.4;
}
.previa-correo {
  width: 100%;
  height: 520px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}
.asunto-previo {
  font-size: 13px;
  margin-bottom: 6px;
  padding: 6px 8px;
  background: #f5f6f8;
  border-radius: 4px;
}
.variables .variable {
  cursor: pointer;
  margin: 2px;
}
.lista-versiones {
  max-height: 55vh;
  overflow: auto;
}
.version {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.barra > * {
  margin-bottom: 6px;
}
</style>
